import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { UseFormRegister, UseFormSetValue } from "react-hook-form"
import Picker, { PickerValue } from "react-mobile-picker"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import ArrowIcon from "components/atoms/_icons/ArrowIcon"
import CTA from "components/atoms/CTA"
import FormField from "components/atoms/FormField"
import BottomSheet from "components/atoms/BottomSheet"

type SelectOptionType = {
  value: string;
  label: string;
}

type SelectProps = {
  id: string;
  label: string;
  options: SelectOptionType[];
  value: string;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  isDeletable: boolean;
  onChange?: (value: any) => void;
}

export default function CustomSelect(props: SelectProps) {
  const {
    id,
    label,
    options,
    value,
    register,
    setValue,
    isDeletable,
    onChange = new Function()
  } = props

  const [showPicker, setShowPicker] = useState(false)
  const [selectedValue, setSelectedValue] = useState<PickerValue>({ [id]: "" })
  const [valueToRender, setValueToRender] = useState<string>("")

  const { t } = useTranslation()

  const handlePickerChange = useCallback((newValue: PickerValue, key: string) => {
    setSelectedValue(newValue)
  }, [])

  useEffect(() => {
    if (!showPicker) return
    updateSelectedDateValue()
  }, [showPicker])

  useEffect(() => {
    if (value) {
      const selectedOption = options.find((option: SelectOptionType) => option.value === value)
      if (selectedOption) {
        setValueToRender(selectedOption.label)
      }
      setValue(id, value, { shouldDirty: true })
    } else {
      setValueToRender("")
      setValue(id, "", { shouldDirty: true })
    }

  }, [value])

  function updateSelectedDateValue() {
    let defaultSelectedOption = options[0].value
    if (value) {
      const selectedOption = options.find((option: SelectOptionType) => option.value === value)
      if (selectedOption) {
        defaultSelectedOption = selectedOption.value
      }
    }
    setSelectedValue({ [id]: defaultSelectedOption })
  }

  function handleSubmit() {
    onChange(selectedValue[id])
    const selectedOption = options.find((option: SelectOptionType) => option.value === selectedValue[id])
    if (selectedOption) {
      setValueToRender(selectedOption.label)
    }
    setShowPicker(false)
  }

  function handleDiscard() {
    setShowPicker(false)
  }

  function toggleShowBottomSheet() {
    setShowPicker(!showPicker)
  }

  function resetValue() {
    onChange("")
    setValueToRender("")
    setSelectedValue({ [id]: "" })
  }


  return (
    <>
      <Container
        id={id}
        {...register(id)}
      >
        <FormField
          id={`${id}-field`}
          label={label}
          value={valueToRender}
          icon={<ArrowIcon/>}
          isDeletable={isDeletable}
          onClick={toggleShowBottomSheet}
          onDeleteIconClick={resetValue}
        />
      </Container>
      <BottomSheet
        id="picker"
        show={showPicker}
        swipeable={false}
        openBottomSheet={() => {
        }}
        closeBottomSheet={toggleShowBottomSheet}
      >
        <Picker
          value={selectedValue}
          onChange={handlePickerChange}
          height={150}
          itemHeight={50}
          wheelMode="natural"
        >
          <Picker.Column name={id} id={id} className="picker-column">
            {options.map((option) => (
              <Picker.Item key={option.value} value={option.value} className="picker-item">
                {({ selected }) => (
                  <div className={selected ? "selected" : ""}>{option.label}</div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>
        </Picker>

        <ButtonsWrapper>
          <CTA
            variant="contained"
            onClick={handleSubmit}
          >
            {t("general.submit")}
          </CTA>
          <CTA
            variant="outlined"
            onClick={handleDiscard}
          >
            {t("general.discard")}
          </CTA>
        </ButtonsWrapper>
      </BottomSheet>
    </>

  )
}

const Container = styled("div")(({ theme }) => ({
  width: "100%",

  "& svg": {
    transform: "rotate(-90deg)"
  }
}))

const ButtonsWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row",
  alignItems: "center",
  gap: "24px",
  marginTop: "24px"
}))

